<template>
    <div>
        <div class="charts-container">
            <div class="chart-container">
                <div class="title-grid">
                    <h3>{{ $gettext('Revenue (ex. VAT)') }}</h3>
                    <CustomerPieChart v-show="!loading" :valueList="revenueList"></CustomerPieChart>
                    <b-skeleton type="avatar" v-if="loading" class="skeleton-size"></b-skeleton>
                </div>
            </div>
            <div class="chart-container">
                <div class="title-grid">
                    <h3>{{ $gettext('Revenue VAT') }}</h3>
                    <CustomerPieChart v-show="!loading" :valueList="revenueVATList"></CustomerPieChart>
                    <b-skeleton type="avatar" v-if="loading" class="skeleton-size"></b-skeleton>
                </div>
            </div>
        </div>

        <GekkoButton
            @click="downloadReport"
            :text="$gettext('Download Report (.csv)')"
            :icon="'fa fa-arrow-down'"
            class="p-0 sort-icon-class"
            variant="link"
        />
        <b-table
            :fields="fields"
            :items="items"
            :class="['gk-table gk-table--responsive gk-table--striped gk-table--headed']"
            :busy="loading"
            ref="report_table"
            :tbody-tr-class="rowClass"
            show-empty
            table-busy>
            <template #cell(name)="row">
                <span>{{ row.value }}</span>
                &nbsp;
                <font-awesome-icon v-if="row.item.is_archived" icon="fa-solid fa-folder" />
            </template>
            <template #head(name)>
                <translate>Customer</translate>
                &nbsp;
                <GekkoButton
                    @click="sortByName"
                    :icon="sortNameDesc ? 'fa fa-arrow-down-z-a' : 'fa fa-arrow-up-z-a'"
                    class="p-0 sort-icon-class"
                    variant="link"
                />
            </template>
            <template #cell(vat)="row">{{ row.value }}</template>
            <template #cell(revenue)="row">{{ formatNumber(row.value) }}</template>
            <template #cell(cost)="row">{{    formatNumber(row.value) }}</template>
            <template #cell(margin)="row">{{  formatNumber(row.value) }}</template>
        </b-table>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ReportsService from '@/store/services/reports.service.js'
import CustomerPieChart from '@/components/customer_insight/CustomerPieChart'
import GekkoButton from '@/components/GekkoButton.vue'

export default {
    name: "MarginTable",
    components: {CustomerPieChart, GekkoButton},
    props: {
        customers: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            items: [],
            fields: [
                { key: 'name', label: this.$gettext('Customer')},
                { key: 'vat', label: this.$gettext('VAT')},
                { key: 'revenue_exvat', label: this.$gettext('Revenue (ex. VAT)'), thClass: 'th-class', tdClass: 'td-class', sortable: true},
                { key: 'revenue_vat', label: this.$gettext('Revenue VAT'), thClass: 'th-class', tdClass: 'td-class', sortable: true},
                { key: 'revenue_total', label: this.$gettext('Total'), thClass: 'th-class', tdClass: 'td-class', sortable: true}
            ],
            sortNameDesc: false,
            loading_customers: true,
            loading_filters: true
        }
    },
    computed: {
        ...mapGetters({ 
            getCompanyId: 'main/getCompanyId'
        }),
        revenueList() {
            return this.items.filter((item) => item.revenue_exvat > 0).map((item) => { return {'label': item.name, 'value': item.revenue_exvat}})
        },
        revenueVATList() {
            return this.items.filter((item) => item.revenue_vat > 0).map((item) => { return {'label': item.name, 'value': item.revenue_vat}})
        },
        loading() {
            return this.loading_customers || this.loading_filters
        }
    },
    watch: {
        customers() {
            this.loading_customers = true
            this.setReportData('customers')
        },
        filters() {
            this.loading_filters = true
            this.setReportData('filters')
        }
    },
    methods: {
        setReportData(loading_type) {
            if (this.$store.getters['getIsPremium']) {
                if (this.customers.length)
                    this.setRealData(loading_type)
            } else
                this.setFakeData(loading_type)
        },
        setRealData(loading_type) {
            let filters_customer_insight = {
                'start_date': this.filters.start_date,
                'end_date': this.filters.end_date,
                'company_profile': this.getCompanyId,
            }
            ReportsService.getCustomerInsightIncomeData(filters_customer_insight).then((response)=>{
                this.items = []
                var temp_data = response.data;
                for(let i = 0; i < temp_data.length; i++) {
                    this.items.push({
                        'id': temp_data[i][0],
                        'name' : temp_data[i][1],
                        'vat' : temp_data[i][2],
                        'revenue_exvat': temp_data[i][3],
                        'revenue_vat': temp_data[i][4],
                        'revenue_total': temp_data[i][3] + temp_data[i][4],
                        'is_archived': temp_data[i][5]
                    })
                }
                this.loading_customers = false
                this.loading_filters = false
            })
        },
        setFakeData(loading_type) {
            this.items = []

            this.items.push({
                "name": this.$gettext("(no customer)"),
                'vat' : "0123456789",
                'revenue_exvat': 10,
                'revenue_vat': 4,
                'revenue_total': 14,
                'is_archived': false
            })

            for(let i = 1; i <= 4; i++) {
                this.items.push({
                    "name": `${this.$gettext("fake customer")} ${i}`,
                    'vat' : "0123456789",
                    "revenue_exvat": (300 * i),
                    "revenue_vat": (200 * i),
                    "revenue_total": ((300 * i) - (200*1))
                })
            }

            if (loading_type === 'customers')
                this.loading_customers = false
            if (loading_type === 'filters')
                this.loading_filters = false
        },
        downloadReport(){
            let filters_customer_insight = {
                'start_date': this.filters.start_date,
                'end_date': this.filters.end_date,
                'company_profile': this.getCompanyId,
            }
            ReportsService.getCustomerInsightIncomeCSV(filters_customer_insight).then((response)=>{
                if(response.status === 200){
                    this.$store.commit('setAlert', {
                        title: this.$gettext('Initialized download'),
                        text: this.$gettext('Check the downloads page to download the report.'),
                        variant: 'success',
                        duration: 4,
                    })
                }
            })
        },
        formatNumber(num) {
            num = parseInt(num.toFixed(0))
            let str = num.toLocaleString('en-US')
            return `€ ${str}`
        },
        sortByName() {
            if (this.sortNameDesc) {
                this.items.sort((a, b) => {
                    const nameA = a.name.toUpperCase()
                    const nameB = b.name.toUpperCase()

                    if (nameA < nameB)
                        return -1
                    
                    if (nameA > nameB)
                        return 1
                    
                    return 0
                })
            } else {
                this.items.sort((a, b) => {
                    const nameA = a.name.toUpperCase()
                    const nameB = b.name.toUpperCase()

                    if (nameA > nameB)
                        return -1

                    if (nameA < nameB)
                        return 1
                    
                    return 0
                })
            }

            this.sortNameDesc = !this.sortNameDesc
        },
        rowClass(item) {
            if (item && item.is_archived)
                return "archived-row"
            return ""
        }
    }
}
</script>

<style scoped>
.charts-container {
    display: flex;
    flex-direction: row;
    padding-top: 1rem;
    padding-bottom: 1rem;
    overflow: auto;
}

.chart-container {
    flex-grow: 1;
    display: flex;
    justify-content: center;
}

.title-grid {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.skeleton-size {
    min-width: 180px;
    width: 300px;
    height: 300px;
}
</style>

<style>
.th-class {
    text-align: right;
}

.td-class {
    text-align: right;
}

.sort-icon-class:hover {
    color: #3f444d;
}

.archived-row {
    color: #bbb;
}
</style>